import React, { useState, useEffect } from 'react';
import axios from "../../utils/axios";
import { getToken } from '../../utils/Common';
import Layout from '../../components/Layout';
import { Building2, MapPin, Phone, Mail, Search, CheckCircle, XCircle, Plus, Edit2, Trash2, X, Save } from 'lucide-react';
import Swal from 'sweetalert2';

function SitesPage() {
  const [sites, setSites] = useState([]);
  const [filteredSites, setFilteredSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentSite, setCurrentSite] = useState(null);
  const [formData, setFormData] = useState({
    nm_hotel: '',
    code_hotel: '',
    addr_hotel: '',
    phone_hotel: '',
    email_hotel: '',
    status_hotel: 0
  });
  
  const token = getToken();
  const headers = { 
    Authorization: `Bearer ${token}`, 
    'Access-Control-Allow-Origin': '*', 
    'Content-Type': 'application/json' 
  };

  // Fetch sites data
  useEffect(() => {
    fetchSites();
  }, []);

  // Filter sites based on search
  useEffect(() => {
    if (searchTerm === '') {
      setFilteredSites(sites);
    } else {
      const filtered = sites.filter(site => 
        site.nm_hotel?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        site.code_hotel?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        site.addr_hotel?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredSites(filtered);
    }
  }, [searchTerm, sites]);

  const fetchSites = async () => {
    try {
      setLoading(true);
      console.log('🏨 Fetching sites from API...');
      console.log('Token:', token ? 'Present' : 'MISSING');
      console.log('Headers:', headers);
      
      const response = await axios.get('/sites', { headers });
      console.log('🏨 API Response:', response);
      console.log('🏨 Response data:', response.data);
      
      if (response.data.success) {
        console.log('✅ Sites data loaded:', response.data.data);
        console.log('Number of sites:', response.data.data.length);
        setSites(response.data.data);
        setFilteredSites(response.data.data);
      } else {
        console.log('❌ API returned success: false');
      }
    } catch (error) {
      console.error('❌ Error fetching sites:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      console.error('Error details:', error.response?.data);
      
      if (error.response?.status === 401) {
        alert('Authentication error. Please login again.');
      } else {
        alert('Failed to load sites. Check console for details.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Open modal for adding new site
  const handleAdd = () => {
    setEditMode(false);
    setCurrentSite(null);
    setFormData({
      nm_hotel: '',
      code_hotel: '',
      addr_hotel: '',
      phone_hotel: '',
      email_hotel: '',
      status_hotel: 0
    });
    setShowModal(true);
  };

  // Open modal for editing site
  const handleEdit = (site) => {
    setEditMode(true);
    setCurrentSite(site);
    setFormData({
      nm_hotel: site.nm_hotel,
      code_hotel: site.code_hotel,
      addr_hotel: site.addr_hotel || '',
      phone_hotel: site.phone_hotel || '',
      email_hotel: site.email_hotel || '',
      status_hotel: site.status_hotel
    });
    setShowModal(true);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Submit form (create or update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editMode) {
        // Update existing site
        await axios.put(`/sites/${currentSite.id_hotel}`, formData, { headers });
        Swal.fire('Success!', 'Hotel updated successfully', 'success');
      } else {
        // Create new site
        await axios.post('/sites', formData, { headers });
        Swal.fire('Success!', 'Hotel added successfully', 'success');
      }
      
      setShowModal(false);
      fetchSites(); // Refresh the list
    } catch (error) {
      console.error('Error saving site:', error);
      Swal.fire('Error!', error.response?.data?.message || 'Failed to save hotel', 'error');
    }
  };

  // Delete site
  const handleDelete = async (site) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Delete ${site.nm_hotel}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`/sites/${site.id_hotel}`, { headers });
        Swal.fire('Deleted!', 'Hotel has been deleted.', 'success');
        fetchSites(); // Refresh the list
      } catch (error) {
        console.error('Error deleting site:', error);
        Swal.fire('Error!', 'Failed to delete hotel', 'error');
      }
    }
  };

  return (
    <Layout title="Sites" subtitle="Hotel & Property List">
      <div className="p-4 sm:p-6 md:p-8">
        
        {/* Header Section */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl mb-6 border border-light-gray dark:border-gray-700">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold text-primary-dark dark:text-white">Hotel Sites</h2>
              <p className="text-sm text-secondary-dark dark:text-gray-400 mt-1">
                Manage all hotel properties and locations
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-sm text-secondary-dark dark:text-gray-400">
                Total Sites: <span className="font-bold text-accent-rose">{filteredSites.length}</span>
              </div>
              <button
                onClick={handleAdd}
                className="flex items-center gap-2 px-4 py-2 bg-accent-rose hover:bg-accent-rose/90 text-white rounded-lg font-semibold transition-colors shadow-md"
              >
                <Plus className="w-5 h-5" />
                <span>Add New</span>
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="mt-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by hotel name, code, or city..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-accent-rose focus:border-accent-rose dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-accent-rose"></div>
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden lg:block bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden border border-light-gray dark:border-gray-700">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Hotel Code
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Hotel Name
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Address
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Contact
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredSites.length > 0 ? (
                      filteredSites.map((site) => (
                        <tr key={site.id_hotel} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <Building2 className="w-5 h-5 text-accent-rose mr-2" />
                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                {site.code_hotel || '-'}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm font-semibold text-gray-900 dark:text-white">
                              {site.nm_hotel}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-start">
                              <MapPin className="w-4 h-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                {site.addr_hotel || '-'}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="space-y-1">
                              {site.phone_hotel && (
                                <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                                  <Phone className="w-4 h-4 mr-2" />
                                  {site.phone_hotel}
                                </div>
                              )}
                              {site.email_hotel && (
                                <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                                  <Mail className="w-4 h-4 mr-2" />
                                  {site.email_hotel}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {site.status_hotel === '0' || site.status_hotel === 0 ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Active
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                <XCircle className="w-4 h-4 mr-1" />
                                Inactive
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleEdit(site)}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Edit"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(site)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                          No sites found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden space-y-4">
              {filteredSites.length > 0 ? (
                filteredSites.map((site) => (
                  <div
                    key={site.id_hotel}
                    className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-lg border border-gray-200 dark:border-gray-700"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center">
                        <Building2 className="w-6 h-6 text-accent-rose mr-2" />
                        <div>
                          <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                            {site.nm_hotel}
                          </h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {site.code_hotel || 'No Code'}
                          </p>
                        </div>
                      </div>
                      {site.status_hotel === '0' || site.status_hotel === 0 ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <XCircle className="w-3 h-3 mr-1" />
                          Inactive
                        </span>
                      )}
                    </div>

                    <div className="space-y-2 mb-3">
                      {site.addr_hotel && (
                        <div className="flex items-start">
                          <MapPin className="w-4 h-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {site.addr_hotel}
                          </span>
                        </div>
                      )}
                      {site.phone_hotel && (
                        <div className="flex items-center">
                          <Phone className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {site.phone_hotel}
                          </span>
                        </div>
                      )}
                      {site.email_hotel && (
                        <div className="flex items-center">
                          <Mail className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {site.email_hotel}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Mobile Actions */}
                    <div className="flex items-center gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <button
                        onClick={() => handleEdit(site)}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                        <span className="font-medium">Edit</span>
                      </button>
                      <button
                        onClick={() => handleDelete(site)}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span className="font-medium">Delete</span>
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  No sites found
                </div>
              )}
            </div>
          </>
        )}

        {/* Add/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 backdrop-blur-md flex items-center justify-center z-50 p-4 animate-fadeIn">
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl max-w-3xl w-full max-h-[95vh] overflow-hidden border-2 border-gray-100 dark:border-gray-700 animate-slideUp">
              {/* Modal Header with Gradient */}
              <div className="bg-gradient-to-r from-accent-rose to-purple-600 p-6 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                      <Building2 className="w-8 h-8" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold">
                        {editMode ? 'Edit Hotel Site' : 'Add New Hotel Site'}
                      </h3>
                      <p className="text-sm text-white/80 mt-1">
                        {editMode ? 'Update hotel information' : 'Fill in the details to add a new hotel'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowModal(false)}
                    className="p-2 hover:bg-white/20 rounded-xl transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <form onSubmit={handleSubmit} className="p-8 overflow-y-auto max-h-[calc(95vh-140px)]">
                {/* Section: Basic Information */}
                <div className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-1 h-6 bg-accent-rose rounded-full"></div>
                    <h4 className="text-lg font-bold text-gray-800 dark:text-white">Basic Information</h4>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Hotel Name */}
                    <div className="md:col-span-2">
                      <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        <Building2 className="w-4 h-4 text-accent-rose" />
                        Hotel Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="nm_hotel"
                        value={formData.nm_hotel}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-accent-rose focus:border-accent-rose dark:bg-gray-700 dark:text-white transition-all"
                        placeholder="e.g., Ashley Hotel Jakarta"
                      />
                    </div>

                    {/* Hotel Code */}
                    <div>
                      <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        <Building2 className="w-4 h-4 text-accent-rose" />
                        Hotel Code <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="code_hotel"
                        value={formData.code_hotel}
                        onChange={handleInputChange}
                        required
                        maxLength={11}
                        className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-accent-rose focus:border-accent-rose dark:bg-gray-700 dark:text-white transition-all uppercase"
                        placeholder="e.g., AHJ"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Max 11 characters</p>
                    </div>

                    {/* Status */}
                    <div>
                      <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        <CheckCircle className="w-4 h-4 text-accent-rose" />
                        Status <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="status_hotel"
                        value={formData.status_hotel}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-accent-rose focus:border-accent-rose dark:bg-gray-700 dark:text-white transition-all"
                      >
                        <option value={0}>✓ Active</option>
                        <option value={1}>✗ Inactive</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Section: Location & Address */}
                <div className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-1 h-6 bg-purple-600 rounded-full"></div>
                    <h4 className="text-lg font-bold text-gray-800 dark:text-white">Location & Address</h4>
                  </div>
                  
                  {/* Address */}
                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      <MapPin className="w-4 h-4 text-purple-600" />
                      Complete Address
                    </label>
                    <textarea
                      name="addr_hotel"
                      value={formData.addr_hotel}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-accent-rose focus:border-accent-rose dark:bg-gray-700 dark:text-white transition-all"
                      placeholder="Enter full address including street, city, postal code"
                    />
                  </div>
                </div>

                {/* Section: Contact Information */}
                <div className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-1 h-6 bg-blue-600 rounded-full"></div>
                    <h4 className="text-lg font-bold text-gray-800 dark:text-white">Contact Information</h4>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Phone */}
                    <div>
                      <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        <Phone className="w-4 h-4 text-blue-600" />
                        Phone Number
                      </label>
                      <input
                        type="text"
                        name="phone_hotel"
                        value={formData.phone_hotel}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-accent-rose focus:border-accent-rose dark:bg-gray-700 dark:text-white transition-all"
                        placeholder="+62 21 1234 5678"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        <Mail className="w-4 h-4 text-blue-600" />
                        Email Address
                      </label>
                      <input
                        type="email"
                        name="email_hotel"
                        value={formData.email_hotel}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-accent-rose focus:border-accent-rose dark:bg-gray-700 dark:text-white transition-all"
                        placeholder="info@hotel.com"
                      />
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-4 pt-6 border-t-2 border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 px-6 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all font-semibold text-lg"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-accent-rose to-purple-600 hover:from-accent-rose/90 hover:to-purple-600/90 text-white rounded-xl transition-all font-semibold text-lg shadow-lg hover:shadow-xl"
                  >
                    <Save className="w-5 h-5" />
                    <span>{editMode ? 'Update Hotel' : 'Save Hotel'}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default SitesPage;
